import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import './ScrapeErrors.css';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5555' : '';

function ScrapeErrors() {
  const { fetchWithAuth } = useAuth();
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [errors, setErrors] = useState([]);
  const [errorsByShop, setErrorsByShop] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [sortField, setSortField] = useState('storeName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [shopFilter, setShopFilter] = useState('all');
  const [view, setView] = useState('overview'); // 'overview' | 'detail'
  const [editingIdx, setEditingIdx] = useState(null); // Index der gerade editierten Zeile
  const [editUrl, setEditUrl] = useState(''); // Editierbarer URL-Wert
  const [saving, setSaving] = useState(false); // Speicher-Loading
  const [successMsg, setSuccessMsg] = useState('');

  // Lade Scrape-Logs Übersicht
  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`${API_BASE}/api/batch-scraper/scrape-logs?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      } else {
        setErrorMsg('Fehler beim Laden der Scrape-Logs');
      }
    } catch (err) {
      setErrorMsg(`Verbindungsfehler: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  // Lade Fehler-Details für einen bestimmten Scrape-Run
  const loadErrors = useCallback(async (logId) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (logId) params.append('logId', logId);
      if (shopFilter !== 'all') params.append('shop', shopFilter);
      params.append('sort', sortField);
      params.append('order', sortOrder);

      const res = await fetchWithAuth(`${API_BASE}/api/batch-scraper/scrape-errors?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setErrors(data.errors || []);
        setErrorsByShop(data.errorsByShop || []);
        setSelectedLog({
          _id: data.logId,
          startedAt: data.startedAt,
          totalUrls: data.totalUrls,
          totalErrors: data.totalErrors,
          errorRate: data.errorRate
        });
      }
    } catch (err) {
      setErrorMsg(`Fehler: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, shopFilter, sortField, sortOrder]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Sortierung umschalten
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Sortier-Icon
  const sortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 60) return `${seconds.toFixed(0)}s`;
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}m ${sec}s`;
  };

  const getErrorRateClass = (rate) => {
    if (rate <= 5) return 'rate-good';
    if (rate <= 15) return 'rate-warning';
    return 'rate-critical';
  };

  // URL bearbeiten starten
  const startEditUrl = (idx, currentUrl) => {
    setEditingIdx(idx);
    setEditUrl(currentUrl || '');
    setSuccessMsg('');
  };

  // URL bearbeiten abbrechen
  const cancelEditUrl = () => {
    setEditingIdx(null);
    setEditUrl('');
  };

  // URL speichern
  const saveUrl = async (err, idx) => {
    if (!editUrl.trim() || !editUrl.startsWith('http')) {
      setErrorMsg('URL muss mit http:// oder https:// beginnen');
      return;
    }
    if (editUrl.trim() === err.url) {
      cancelEditUrl();
      return;
    }
    try {
      setSaving(true);
      setErrorMsg('');
      const res = await fetchWithAuth(`${API_BASE}/api/products/${err.productId}/shop-url/${err.storeId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newUrl: editUrl.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // URL in der lokalen Fehler-Liste aktualisieren
        setErrors(prev => prev.map((e, i) => 
          i === idx ? { ...e, url: editUrl.trim() } : e
        ));
        setEditingIdx(null);
        setEditUrl('');
        
        // Scrape-Ergebnis anzeigen
        if (data.scrapeResult?.success) {
          setSuccessMsg(`✅ URL für "${err.productName}" aktualisiert — neuer Preis: €${data.scrapeResult.price.toFixed(2)}`);
        } else {
          setSuccessMsg(`✅ URL aktualisiert für "${err.productName}" — ⚠️ Preis konnte nicht abgerufen werden: ${data.scrapeResult?.error || 'unbekannt'}`);
        }
        setTimeout(() => setSuccessMsg(''), 8000);
      } else {
        setErrorMsg(data.message || 'Fehler beim Speichern der URL');
      }
    } catch (e) {
      setErrorMsg(`Speichern fehlgeschlagen: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Detail-Ansicht
  if (view === 'detail') {
    return (
      <div className="scrape-errors">
        <div className="scrape-errors-header">
          <button className="btn-back" onClick={() => { setView('overview'); setErrors([]); setShopFilter('all'); }}>
            ← Zurück zur Übersicht
          </button>
          <h2>🔍 Fehler-Details</h2>
          {selectedLog && (
            <div className="detail-summary">
              <span className="detail-date">{formatDate(selectedLog.startedAt)}</span>
              <span className={`detail-rate ${getErrorRateClass(selectedLog.errorRate)}`}>
                {selectedLog.totalErrors} Fehler / {selectedLog.totalUrls} URLs ({selectedLog.errorRate}%)
              </span>
            </div>
          )}
        </div>

        {/* Shop-Fehler Übersicht */}
        {errorsByShop && errorsByShop.length > 0 && (
          <div className="shop-errors-summary">
            <h3>Fehler pro Shop</h3>
            <div className="shop-error-cards">
              {errorsByShop.filter(s => s.errorCount > 0).map((shop, idx) => (
                <div 
                  key={idx} 
                  className={`shop-error-card ${shopFilter === shop.storeName ? 'active' : ''}`}
                  onClick={() => setShopFilter(shopFilter === shop.storeName ? 'all' : shop.storeName)}
                >
                  <div className="shop-error-name">{shop.storeName}</div>
                  <div className="shop-error-stats">
                    <span className="shop-error-count">{shop.errorCount}</span>
                    <span className="shop-error-total">/ {shop.totalUrls}</span>
                  </div>
                  <div className={`shop-error-rate ${getErrorRateClass(shop.errorRate)}`}>
                    {shop.errorRate}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="error-filters">
          <div className="filter-group">
            <label>Shop-Filter:</label>
            <select value={shopFilter} onChange={e => setShopFilter(e.target.value)}>
              <option value="all">Alle Shops</option>
              {errorsByShop && errorsByShop.filter(s => s.errorCount > 0).map((shop, idx) => (
                <option key={idx} value={shop.storeName}>{shop.storeName} ({shop.errorCount})</option>
              ))}
            </select>
          </div>
          <button className="btn-refresh" onClick={() => loadErrors(selectedLog?._id)}>
            🔄 Aktualisieren
          </button>
        </div>

        {/* Erfolgs-Meldung */}
        {successMsg && <div className="success-message">{successMsg}</div>}

        {/* Fehler-Tabelle */}
        <div className="errors-table-container">
          <table className="errors-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('productName')} className="sortable">
                  Produkt {sortIcon('productName')}
                </th>
                <th onClick={() => toggleSort('storeName')} className="sortable">
                  Shop {sortIcon('storeName')}
                </th>
                <th onClick={() => toggleSort('error')} className="sortable">
                  Fehler {sortIcon('error')}
                </th>
                <th>URL</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="loading-cell">Lade Fehler...</td></tr>
              ) : errors.length === 0 ? (
                <tr><td colSpan="5" className="empty-cell">Keine Fehler {shopFilter !== 'all' ? `für ${shopFilter}` : ''}</td></tr>
              ) : (
                errors.map((err, idx) => (
                  <tr key={idx} className={editingIdx === idx ? 'row-editing' : ''}>
                    <td className="cell-product">{err.productName}</td>
                    <td className="cell-shop">{err.storeName}</td>
                    <td className="cell-error">
                      <span className="error-badge">{err.error}</span>
                    </td>
                    <td className="cell-url">
                      {editingIdx === idx ? (
                        <div className="url-edit-inline">
                          <input
                            type="url"
                            className="url-edit-input"
                            value={editUrl}
                            onChange={e => setEditUrl(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveUrl(err, idx);
                              if (e.key === 'Escape') cancelEditUrl();
                            }}
                            placeholder="https://..."
                            autoFocus
                            disabled={saving}
                          />
                        </div>
                      ) : (
                        <a href={err.url} target="_blank" rel="noopener noreferrer" title={err.url}>
                          {err.url?.length > 50 ? err.url.substring(0, 50) + '...' : err.url}
                        </a>
                      )}
                    </td>
                    <td className="cell-actions">
                      {editingIdx === idx ? (
                        <div className="url-edit-actions">
                          <button
                            className="btn-save-url"
                            onClick={() => saveUrl(err, idx)}
                            disabled={saving || !editUrl.trim()}
                            title="Speichern (Enter)"
                          >
                            {saving ? '⏳' : '💾'}
                          </button>
                          <button
                            className="btn-cancel-url"
                            onClick={cancelEditUrl}
                            disabled={saving}
                            title="Abbrechen (Esc)"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn-edit-url"
                          onClick={() => startEditUrl(idx, err.url)}
                          title="URL bearbeiten"
                        >
                          ✏️
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {errors.length > 0 && (
            <div className="table-footer">
              {errors.length} Fehler angezeigt {shopFilter !== 'all' ? `(gefiltert: ${shopFilter})` : ''}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Übersichtsansicht
  return (
    <div className="scrape-errors">
      <div className="scrape-errors-header">
        <h2>❌ Kein-Preis Fehler Monitor</h2>
        <p className="header-subtitle">Übersicht der Batch-Scrape Ergebnisse und Fehlerquoten</p>
      </div>

      {errorMsg && <div className="error-message">{errorMsg}</div>}

      {loading ? (
        <div className="loading-state">Lade Scrape-Logs...</div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p>Noch keine Scrape-Logs vorhanden.</p>
          <p className="empty-hint">Scrape-Logs werden automatisch beim nächsten Batch-Scrape erstellt.</p>
        </div>
      ) : (
        <>
          {/* Fehlerquoten-Trend */}
          <div className="trend-section">
            <h3>📈 Fehlerquoten-Trend (letzte Scrapes)</h3>
            <div className="trend-bars">
              {logs.slice(0, 10).reverse().map((log, idx) => {
                const maxErrors = Math.max(...logs.slice(0, 10).map(l => l.totalErrors || 0), 1);
                const barHeight = Math.max(((log.totalErrors || 0) / maxErrors) * 100, 5);
                return (
                  <div key={idx} className="trend-bar-wrapper" title={`${formatDate(log.startedAt)}: ${log.totalErrors} Fehler (${log.errorRate}%)`}>
                    <div className="trend-bar-label">{log.errorRate || 0}%</div>
                    <div 
                      className={`trend-bar ${getErrorRateClass(log.errorRate || 0)}`}
                      style={{ height: `${barHeight}%` }}
                    />
                    <div className="trend-bar-date">
                      {new Date(log.startedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scrape-Runs Tabelle */}
          <div className="logs-table-container">
            <h3>📋 Scrape-Runs</h3>
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Status</th>
                  <th>URLs</th>
                  <th>Erfolg</th>
                  <th>Fehler</th>
                  <th>Fehlerquote</th>
                  <th>Dauer</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id} className={`log-row ${log.status}`}>
                    <td>{formatDate(log.startedAt)}</td>
                    <td>
                      <span className={`status-badge status-${log.status}`}>
                        {log.status === 'completed' ? '✅' : log.status === 'stopped' ? '⛔' : log.status === 'running' ? '🔄' : '❌'}
                        {' '}{log.status}
                      </span>
                    </td>
                    <td>{log.totalUrls}</td>
                    <td className="cell-success">{log.totalSuccess}</td>
                    <td className="cell-errors">{log.totalErrors}</td>
                    <td>
                      <span className={`rate-badge ${getErrorRateClass(log.errorRate || 0)}`}>
                        {(log.errorRate || 0).toFixed(1)}%
                      </span>
                    </td>
                    <td>{formatDuration(log.duration)}</td>
                    <td>
                      <button 
                        className="btn-view-errors"
                        onClick={() => {
                          setView('detail');
                          loadErrors(log._id);
                        }}
                        disabled={!log.totalErrors}
                      >
                        🔍 Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default ScrapeErrors;
